/**
 * Shared error pattern utilities for tuition error banners
 */

export interface ErrorPattern {
    includes: string[];
    message: (label: string, prefix?: string) => string;
}

/**
 * Build class prefix for error messages
 */
export const buildClassPrefix = (
    isClassField: boolean,
    classNumber: number,
    currentStep: number
): string => {
    if (currentStep === 5 && isClassField) {
        return ` of Class ${classNumber}`;
    }
    if (currentStep === 5 && !isClassField) {
        return ' of Class 1';
    }
    return '';
};

/**
 * Error message patterns with dynamic template functions
 */
export const getErrorPatterns = (): ErrorPattern[] => [
    {
        includes: ['required', 'is required'],
        message: (label, prefix) => `${label}${prefix} is required.`,
    },
    {
        includes: ['No estimated fees comment is required'],
        message: (label) =>
            `${label} is required when no fees are entered in the class section.`,
    },
    {
        includes: ['10-digit phone number', 'Invalid phone number'],
        message: (label, prefix) =>
            `${label}${prefix} is not valid. Enter a 10-digit number without spaces or special characters.`,
    },
    {
        includes: ['Invalid email'],
        message: (label, prefix) =>
            `${label}${prefix} is not valid. Enter a valid email address.`,
    },
    {
        includes: ['MM/DD/YYYY', 'date format'],
        message: (label, prefix) =>
            `${label}${prefix} must be in a valid date format. Use MM/DD/YYYY.`,
    },
    {
        includes: ['future'],
        message: (label, prefix) => `${label}${prefix} must be in the future.`,
    },
    {
        includes: ['must not exceed', 'characters'],
        message: (label, prefix) =>
            `${label}${prefix} must not exceed 500 characters.`,
    },
    {
        includes: ['Enter a valid numeric amount.'],
        message: (label, prefix) =>
            `${label}${prefix} must contain numbers only.`,
    },
];

/**
 * Match error message against patterns and return formatted message
 */
export const formatErrorMessage = (
    errorMessage: string,
    fieldLabel: string,
    classPrefix: string
): string => {
    const patterns = getErrorPatterns();

    for (const pattern of patterns) {
        if (pattern.includes.some((p) => errorMessage.includes(p))) {
            return pattern.message(fieldLabel, classPrefix);
        }
    }

    return `${fieldLabel}${classPrefix}: ${errorMessage}`;
};
